import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { buildAddonRequest, selectionTotal } from './px-addon-cart.js';

class ProductAddons extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal });
    } else {
      this.initialize();
    }
  }

  initialize() {
    if (!this.isConnected) return;
    const { signal } = this.abort;
    this.details = this.closest('.product-details');
    this.section = this.closest('.shopify-section');
    this.variants = JSON.parse(this.querySelector('[data-main-variants]').textContent);
    if (!this.details || this.details.querySelector('px-product-addons') !== this) return;
    if (!this.form) {
      this.formObserver?.disconnect();
      this.formObserver = new MutationObserver(() => {
        if (this.form) { this.formObserver.disconnect(); this.initialize(); }
      });
      this.formObserver.observe(this.details, { childList: true, subtree: true });
      return;
    }
    this.formObserver?.disconnect();
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency', currency: this.dataset.currency || 'USD', currencyDisplay: 'code',
    });
    this.addEventListener('change', () => { this.message(''); this.update(); }, { signal });
    this.details.addEventListener('input', () => this.update(), { signal });
    this.details.addEventListener('quantity-selector:update', () => this.update(), { signal });
    this.section.addEventListener(StandardEvents.productSelect, (event) => this.variantChanged(event), { signal });
    this.hidden = false;
    this.update();
  }

  disconnectedCallback() {
    this.formObserver?.disconnect();
    this.abort?.abort();
    this.details?.removeAttribute('data-px-addons-selected');
  }

  get form() {
    return this.details?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"] form`);
  }

  get checked() { return [...this.querySelectorAll('[data-addon-check]:checked')]; }

  get extras() {
    return this.checked.map(check => {
      const option = check.closest('[data-addon-row]').querySelector('[data-addon-variant]')?.selectedOptions[0];
      return { id: option?.value, price: Number(option?.dataset.price || 0), quantity: Number(option?.dataset.quantity || 1) };
    });
  }

  message(text, error = false, showCart = false) {
    const status = this.querySelector('[data-addon-status]');
    status.textContent = text;
    status.dataset.error = String(error);
    this.querySelector('[data-addon-cart]').hidden = !showCart;
  }

  update() {
    const form = this.form;
    if (!form) return;
    const data = new FormData(form);
    const variant = this.variants.find(v => String(v.id) === String(data.get('id')));
    for (const row of this.querySelectorAll('[data-addon-row]')) {
      const option = row.querySelector('select')?.selectedOptions[0];
      if (option?.value) {
        row.querySelector('[data-addon-price]').textContent = this.money.format(Number(option.dataset.price) / 100);
        row.querySelector('[data-addon-quantity]').textContent = `Qty: ${option.dataset.quantity}`;
      }
    }
    const incomplete = this.extras.some(extra => !extra.id);
    const quantity = Number(data.get('quantity') || 1);
    this.querySelector('[data-addon-total]').textContent = this.variantPending ? 'Updating…' :
      incomplete ? 'Choose options' : variant && quantity > 0 ?
      this.money.format(selectionTotal(variant.price, quantity, this.extras) / 100) : 'Choose product options';
    this.querySelector('.px-addons__summary').hidden = this.checked.length === 0;
    this.details.toggleAttribute('data-px-addons-selected', this.checked.length > 0);
  }

  async variantChanged(event) {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = (this.generation || 0) + 1;
    this.generation = generation;
    this.variantPending = true;
    this.update();
    try {
      const result = await event.promise;
      if (generation !== this.generation) return;
      this.variantError = !result?.detail?.resource;
      // Combined listings must not retain another product's add-ons.
      const productId = result?.detail?.newProduct?.id || result?.detail?.productId;
      if (productId && String(productId) !== this.dataset.productId) {
        this.checked.forEach(check => { check.checked = false; });
        this.hidden = true;
      }
      // Native listeners synchronise form fields before the next animation frame.
      await new Promise(resolve => requestAnimationFrame(resolve));
    } catch {
      if (generation !== this.generation) return;
      this.variantError = true;
      this.message('The product option could not be updated. Please choose it again.', true);
    } finally {
      if (generation === this.generation) { this.variantPending = false; this.update(); }
    }
  }

  lock() {
    const nodes = [...this.querySelectorAll('input, select'), ...this.form.querySelectorAll('button[type=submit], input[name=quantity]')];
    const states = nodes.map(node => [node, node.disabled]);
    nodes.forEach(node => { node.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    return () => {
      states.forEach(([node, disabled]) => { if (node.isConnected) node.disabled = disabled; });
      // A variant may have changed while the request was in flight.
      const variant = this.variants.find(v => String(v.id) === this.form?.querySelector('[name=id]')?.value);
      if (!variant?.available) this.form?.querySelectorAll('button[type=submit]').forEach(button => { button.disabled = true; });
      this.removeAttribute('aria-busy');
    };
  }

  async submit(event) {
    if (!this.checked.length && !this.busy) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy) return;
    if (this.variantPending) { this.message('Please wait for your product option to finish updating.', true); return; }
    if (this.variantError) { this.message('Please choose your product option again before adding to cart.', true); return; }
    const form = this.form;
    if (!form.reportValidity()) return;
    const component = form.closest('product-form-component');
    if (form.querySelector('[ref=addToCartButton]')?.disabled) return;
    const validation = component.refs?.quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) { this.message('This quantity exceeds the available stock. Please reduce it.', true); return; }
    let request;
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId);
      request = buildAddonRequest(new FormData(form), this.extras, sectionIds, window.location.pathname);
    } catch (error) {
      this.message(error.message, true);
      this.checked.map(check => check.closest('[data-addon-row]').querySelector('select')).find(select => !select.value)?.focus();
      return;
    }
    this.busy = true;
    const unlock = this.lock();
    this.message('Adding your selection…');
    const pending = CartLinesUpdateEvent.createPromise();
    // Always settle the theme's event promise, including inventory and transport errors.
    pending.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: request.lines, promise: pending.promise }));
    let accepted = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', body: request.body, headers: { Accept: 'application/json' } });
      const added = await response.json();
      const failed = !response.ok || Boolean(added.status);
      accepted = !failed;
      // Shopify may adjust quantities even on an inventory error; always read the real cart.
      const cartUrl = new URL(`${window.Shopify.routes.root}cart.js`, window.location.origin);
      // Keep unpublished myshopify previews on their working preview host.
      if (window.Shopify.theme?.role === "unpublished" && window.location.hostname.endsWith(".myshopify.com")) {
        cartUrl.searchParams.set("_fd", "0");
      }
      const cartResponse = await fetch(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      pending.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: added.sections, source: 'px-product-addons', productId: this.dataset.productId, itemCount: request.itemCount, didError: failed },
      });
      this.message(failed ? `${added.description || added.message || 'Some items could not be added.'} Check your cart before trying again.` : 'Your selection has been added to your cart.', failed, true);
    } catch (error) {
      pending.reject(error);
      this.message(accepted ? 'Your items were added, but the cart preview could not refresh. View your cart to continue.' : 'We could not confirm the result. Check your cart before trying again to avoid adding items twice.', !accepted, true);
    } finally {
      this.busy = false;
      unlock();
      this.update();
    }
  }
}
if (!customElements.get('px-product-addons')) customElements.define('px-product-addons', ProductAddons);
